import { useEffect } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { ArrowRight, X } from 'lucide-preact'
import { PersonAvatar } from '@/components/common/Avatar'
import { routes } from '@/utils/routes'
import { formatDateTime } from '@/components/webs/email-blasts/engagementFormat'
import { EmailStatusBadge } from './EmailStatusBadge'
import { buildEmailMilestones } from './emailActivityFormat'
import type { EmailActivityRowDto } from '@/types/emailActivity'

interface Props {
    row: EmailActivityRowDto
    clientCompanyId: string
    workspaceId: string
    onClose: () => void
}

// Read-only email-send detail peek (ORB-385): the send's status timeline + engagement, with jumps to the
// recipient's contact (ORB-383) and its broadcast (ORB-384). Remediation lives in the broadcast/activation,
// not here. Drawer chrome mirrors the ORB-383 ContactPeekFlyout. The row already carries everything, so
// there is no fetch.
export function EmailActivityDetailFlyout({ row, clientCompanyId, workspaceId, onClose }: Props) {
    const { route } = useLocation()

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [onClose])

    const scoped = routes.scoped(clientCompanyId, workspaceId)
    const anchorType = row.LeadId ? 'lead' : row.FileImportRowId ? 'audience' : null
    const anchorId = row.LeadId ?? row.FileImportRowId ?? null
    const contactHref = anchorType && anchorId && row.CampaignId
        ? scoped.campaignContact(row.CampaignId, anchorType, anchorId, 'emails')
        : null
    const broadcastHref = row.BlastId && row.CampaignId && row.WebId
        ? scoped.webBroadcast(row.CampaignId, row.WebId, row.BlastId)
        : null
    const openContact = () => { if (contactHref) route(contactHref) }

    const milestones = buildEmailMilestones(row)
    const context = [row.CampaignName, row.WebName].filter(Boolean).join(' · ')

    const toneText = (tone?: 'primary' | 'danger') =>
        tone === 'primary' ? 'text-primary-700 dark:text-primary-400'
            : tone === 'danger' ? 'text-red-700 dark:text-red-400'
                : 'text-slate-700 dark:text-slate-200'
    const toneDot = (tone?: 'primary' | 'danger') =>
        tone === 'primary' ? 'bg-primary-500' : tone === 'danger' ? 'bg-red-500' : 'bg-slate-300 dark:bg-slate-600'

    return (
        <div class="fixed inset-0 z-50 flex" aria-modal="true" role="dialog">
            <div class="flex-1 bg-slate-900/40" onClick={onClose} />
            <aside class="ml-auto flex h-full w-full flex-col border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl sm:w-[440px]">
                <div class="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-5 py-3">
                    <span class="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Email detail</span>
                    <button type="button" onClick={onClose} class="grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>

                <div class="flex-1 space-y-5 overflow-y-auto p-5">
                    {/* recipient */}
                    <div class="flex items-start gap-3">
                        <PersonAvatar fullName={row.RecipientName || row.RecipientEmail} size="lg" />
                        <div class="min-w-0 flex-1">
                            <h2 class="truncate text-base font-semibold text-slate-900 dark:text-white">{row.RecipientName || row.RecipientEmail}</h2>
                            <p class="truncate text-sm text-slate-500 dark:text-slate-400">{row.RecipientEmail}</p>
                            {contactHref && (
                                <button type="button" onClick={openContact} class="mt-1 inline-flex items-center gap-1 text-sm font-medium text-primary-700 dark:text-primary-400 hover:underline">
                                    Open contact <ArrowRight size={13} />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* email summary */}
                    <dl class="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm dark:divide-slate-800/70 dark:border-slate-800">
                        <div class="flex items-center justify-between gap-3 px-3 py-2"><dt class="text-slate-500 dark:text-slate-400">Subject</dt><dd class="truncate text-right font-medium text-slate-800 dark:text-slate-200">{row.Subject || '—'}</dd></div>
                        <div class="flex items-center justify-between gap-3 px-3 py-2"><dt class="text-slate-500 dark:text-slate-400">Type</dt><dd><span class="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500 dark:bg-slate-800 dark:text-slate-400">{row.EmailType}</span></dd></div>
                        <div class="flex items-center justify-between gap-3 px-3 py-2"><dt class="text-slate-500 dark:text-slate-400">Status</dt><dd><EmailStatusBadge status={row.Status} /></dd></div>
                        {broadcastHref && (
                            <div class="flex items-center justify-between gap-3 px-3 py-2"><dt class="text-slate-500 dark:text-slate-400">Broadcast</dt><dd><button type="button" onClick={() => route(broadcastHref)} class="font-medium text-primary-700 hover:underline dark:text-primary-400">View →</button></dd></div>
                        )}
                        {context && (
                            <div class="flex items-center justify-between gap-3 px-3 py-2"><dt class="text-slate-500 dark:text-slate-400">Context</dt><dd class="truncate text-right text-slate-600 dark:text-slate-300">{context}</dd></div>
                        )}
                    </dl>

                    {/* status timeline */}
                    {milestones.length > 0 && (
                        <div>
                            <h3 class="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Status</h3>
                            <ol class="space-y-2.5 text-sm">
                                {milestones.map((m, i) => (
                                    <li key={i} class="flex items-start gap-2">
                                        <span class={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${toneDot(m.tone)}`} />
                                        <span class="min-w-0">
                                            <span class={`font-medium ${toneText(m.tone)}`}>{m.label}</span>
                                            {m.note && <span class="text-slate-500 dark:text-slate-400"> · {m.note}</span>}
                                            {m.at && <span class="ml-1 text-xs tabular-nums text-slate-400 dark:text-slate-500">{formatDateTime(m.at)}</span>}
                                        </span>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}

                    {/* engagement — clicks privileged, opens caveated (inbox privacy) */}
                    {(row.ClickCount > 0 || row.OpenCount > 0) && (
                        <div class="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                            {row.ClickCount > 0 && <span class="text-primary-700 dark:text-primary-400"><span class="font-semibold tabular-nums">{row.ClickCount}</span> click{row.ClickCount === 1 ? '' : 's'}</span>}
                            {row.OpenCount > 0 && <span class="text-slate-600 dark:text-slate-300"><span class="font-semibold tabular-nums">{row.OpenCount}</span>{' '}<span class="cursor-help underline decoration-dotted underline-offset-2" title="Inflated by inbox privacy (e.g. Apple Mail). Clicks are the reliable signal.">opens</span></span>}
                        </div>
                    )}
                </div>

                <div class="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800">
                    <button type="button" onClick={onClose} class="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800">Close</button>
                    {contactHref && (
                        <button type="button" onClick={openContact} class="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-primary-700">
                            Open contact <ArrowRight size={14} />
                        </button>
                    )}
                </div>
            </aside>
        </div>
    )
}
